import CloseIcon from '@mui/icons-material/Close';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Avatar from 'components/Avatar/Avatar';
import IconButton from 'components/IconButton/IconButton';
import Spin from 'components/Spin';
import TextField from 'components/form/TextInput';
import useDebounce from 'hooks/useDebounce';
import useRequest from 'hooks/useRequest';
import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getContacts } from 'services/contacts.service';
import { getCallers } from 'services/ivr.service';
import { addExternalParticipant, callAddPerson } from 'services/ivr.service';
import { formatPhoneNumber, getName } from 'utils';
import toast from 'utils/toast';

const CallTransfer = ({ onClose, onAdded, callSid }) => {
  const account = useSelector((s) => s.settings.account);
  const [callers, { loading }] = useRequest(getCallers, {
    account_id: account.account_id,
  });
  const [query, setQuery] = useState('');
  const [contacts, setContacts] = useState([]);
  const [calling, setCalling] = useState(false);
  const debounceQuery = useDebounce(query, 300);

  useEffect(() => {
    if (!debounceQuery) {
      setContacts([]);
      return;
    }
    getContacts({
      search: debounceQuery,
      having_phone: 1,
    }).then((resp) => {
      if (resp.length) {
        setContacts(resp);
      } else {
        setContacts([
          {
            hash: 'external',
            contact_first_name: 'External number',
            primary_phone: debounceQuery,
            number: debounceQuery,
          },
        ]);
      }
    });
  }, [debounceQuery]);

  const onSelect = async (caller) => {
    try {
      setCalling(true);
      if (caller.isCaller) {
        await callAddPerson(caller.hash, callSid);
      } else {
        await addExternalParticipant(caller.number, callSid);
      }
      toast.success('Call transferred successfully');
      onAdded && onAdded();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCalling(false);
    }
  };

  const listItems = useMemo(() => {
    const contactItems = contacts.map((i) => ({
      hash: i.hash,
      label: getName(i),
      number: i.primary_phone,
    }));

    if (contactItems.length) return contactItems;

    if (!callers || !callers.length) return [];

    return callers
      .map(({ user, hash, is_available }) => {
        const name = `${user.user_first_name} ${user.user_last_name}`;
        return {
          label: name,
          hash,
          number: user.user_mobile,
          disabled: is_available === false,
          isCaller: true,
        };
      })
      .filter(({ label }) => label.toLowerCase().includes(query.toLowerCase()));
  }, [callers, query, contacts]);

  const items = listItems.map(({ label, hash, number, disabled, isCaller }) => (
    <ListItem key={hash}>
      <ListItemAvatar sx={{ minWidth: 45 }}>
        <Avatar>{label}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontWeight: 500,
        }}
        secondary={
          disabled ? 'Not available right now' : formatPhoneNumber(number)
        }
        secondaryTypographyProps={{
          color: disabled ? 'error.main' : 'text.secondary',
        }}
      />
      <ListItemSecondaryAction>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={() => onSelect({ hash, number, isCaller })}
          variant="tonal"
          icon={<PhoneForwardedIcon />}
        />
      </ListItemSecondaryAction>
    </ListItem>
  ));

  return (
    <Box display="flex" flexDirection="column" height={500} overflow="hidden">
      <Stack
        p={2}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography fontWeight={500}>Transfer call</Typography>
        <IconButton icon={<CloseIcon />} onClick={onClose} />
      </Stack>
      <Stack px={2}>
        <TextField
          fullWidth
          value={query}
          size="small"
          onChange={(e) => setQuery(e.target.value)}
          startAdornment={<SearchIcon />}
          placeholder="Start typing name or number"
        />
      </Stack>
      <Spin spinning={loading || calling} tip="Transferring...">
        <List disablePadding sx={{ overflow: 'auto', flex: 1 }}>
          {items}
        </List>
      </Spin>
    </Box>
  );
};

export default CallTransfer;
